import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DataService from '../../services/DataService';
import UnifiedDataManager from '../../services/UnifiedDataManager';
import { resolveMonsterArtUrl } from '../../services/ArtworkResolver';
// date_of_change: 2025-11-09 - Lint cleanup: remove unused imports (normalizer/HP bridge/validator)
import { logIO, logValidation } from '../../utils/DebugLogger';
import {
  convertCRToNumber,
  calculateXPFromCR,
  
  getAbilityModifier,
  formatModifier,
  formatAttackType,
  formatSaveType,
  safeFormat,
  formatDamage
} from '../../utils/MonsterDataFormatters';
import {
  buildShortAbilityBlock,
  resolveCombatantChallengeDisplay,
  resolvePreferredAbilityScores
} from '../../utils/combat/CombatantNormalizer';
import './MonsterSheetViewer.css';

// Using utility function from MonsterDataFormatters.js

// Using utility function from MonsterDataFormatters.js

// Using utility function from MonsterDataFormatters.js

/**
 * Monster Sheet Viewer Component
 * 
 * Displays monster stat blocks in D&D 5e format using real DataService templates.
 * Shows abilities, combat stats, actions, and special abilities.
 * Now uses authentic D&D 5e data from DataService instead of mock data.
 */

// Helper function to get monster template by ID using UnifiedDataManager → DataService fallback
// date_of_change: 2026-04-27 - Reorder: try local DataService (bundled JSON) first to avoid
// unnecessary 404s from the backend API for preloaded SRD monsters that aren't stored remotely.
// UDM backend fetch is now the fallback, eliminating spurious console 404 noise.
const getMonsterTemplate = async (templateId) => {
  if (!templateId) {
    return null;
  }

  // 1) Try local bundled data first (DataService → MonsterDataLoader static imports)
  //    This avoids the 404 from the backend API for preloaded SRD monsters.
  try {
    const monster = await DataService.getMonsterData(templateId);
    if (monster) {
      console.log('[MonsterSheetViewer] Loaded monster from DataService (local):', monster.name);
      return monster;
    }
  } catch (dsErr) {
    // Not found locally — fall through to remote
  }

  // 2) Try UnifiedDataManager (backend API + localStorage + data files)
  try {
    const entity = await UnifiedDataManager.loadEntity(templateId, 'monster');
    if (entity && entity.data) {
      console.log('[MonsterSheetViewer] Loaded monster from UDM:', entity.data.name);
      return entity.data;
    }
  } catch (udmErr) {
    console.warn('[MonsterSheetViewer] UnifiedDataManager load failed:', udmErr?.message);
  }

  // 3) Try alternative ID variations for backward compatibility
  const variations = [
    templateId.toLowerCase(),
    templateId.replace(/\s+/g, '_'),
    templateId.replace(/\s+/g, '_').toLowerCase(),
    templateId.replace(/_/g, ' '),
    templateId.replace(/_/g, ' ').toLowerCase(),
  ];

  for (const variation of variations) {
    try {
      const dsMonster = await DataService.getMonsterData(variation);
      if (dsMonster) {
        console.log('[MonsterSheetViewer] Found match with variation via DataService:', variation, '→', dsMonster.name);
        return dsMonster;
      }
    } catch (e) {
      // Continue trying other variations
    }
    try {
      const entity = await UnifiedDataManager.loadEntity(variation, 'monster');
      if (entity && entity.data) {
        console.log('[MonsterSheetViewer] Found match with variation via UDM:', variation, '→', entity.data.name);
        return entity.data;
      }
    } catch (e) {
      // Continue trying other variations
    }
  }

  console.log('[MonsterSheetViewer] No match found for any variation of:', templateId);
  return null;
};

const firstPresentValue = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
      continue;
    }
    return value;
  }
  return undefined;
};

const toFiniteNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (value == null || value === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toDisplayLabel = (value) => {
  const text = firstPresentValue(value);
  if (!text) return '';
  return String(text).replace(/\b([a-z])/gi, (match) => match.toUpperCase());
};

const MonsterSheetViewer = ({ token = null, templateId = null }) => {
  // date_of_change: 2026-04-27 - Removed tabbed layout; all sections now render in one scrollable stat block
  const [templateData, setTemplateData] = useState(null);
  // date_of_change: 2026-04-28 - Track portrait image load failure to remove container from DOM
  const [portraitError, setPortraitError] = useState(false);
  // @date_of_change: 2025-11-09 - Lint cleanup: removed unused validationStatus state
  
  // Get route parameter if available
  const routeParams = useParams();
  const routeId = routeParams.id;

  // date_of_change: 2026-04-27 - Standalone page mode: when rendered via /monster/:id route
  // (routeId present, no token prop), wrap the card in a full-page shell with navigation.
  const navigate = useNavigate();
  const isStandalonePage = Boolean(routeId && !token);
  
  // Get the template ID from props, URL param, or token data
  const effectiveTemplateId = useMemo(() => {
    // Extract template ID from different potential sources with enhanced logging
    console.log('[MonsterSheetViewer] Resolving template ID from sources:', {
      explicitTemplateId: templateId,
      routeId,
      tokenTemplateId: token?.templateId,
      tokenDashboardId: token?.dashboardId,
      tokenSource: token?.source
    });
    
    // Priority: explicit templateId prop > route param > token.templateId > token.dashboardId
    return templateId || routeId || token?.templateId || token?.dashboardId || null;
  }, [templateId, routeId, token]);

  // Debug logging
  useEffect(() => {
    if (routeId) {
      logIO(`MonsterSheetViewer using route parameter: ${routeId}`);
    }
    if (templateId) {
      logIO(`MonsterSheetViewer using templateId prop: ${templateId}`);
    }
    if (token) {
      logIO(`MonsterSheetViewer using token prop: ${token.name || token.id}`);
    }
    logIO(`MonsterSheetViewer effective templateId: ${effectiveTemplateId}`);
  }, [routeId, templateId, token, effectiveTemplateId]);

  // date_of_change: 2026-04-28 - Reset portrait error when the displayed monster changes
  useEffect(() => {
    setPortraitError(false);
  }, [effectiveTemplateId, token?.id]);

  // Fetch template data when templateId or route param changes
  useEffect(() => {
    // Only fetch if we have an ID and no token data
    // (token data has priority over template)
    if (effectiveTemplateId && (!token || Object.keys(token).length === 0)) {
      const fetchTemplateData = async () => {
        try {
          console.log(`[MonsterSheetViewer] Fetching template data for: ${effectiveTemplateId}`);
          const template = await getMonsterTemplate(effectiveTemplateId);
          console.log(`[MonsterSheetViewer] Successfully fetched template:`, template?.name);
          setTemplateData(template);
        } catch (error) {
          console.error(`[MonsterSheetViewer] Error fetching template data:`, error);
          setTemplateData(null);
        }
      };
      
      fetchTemplateData();
    }
  }, [effectiveTemplateId, token]);
  
  // Memoized monster data processing with validation
  // @date_of_change 2025-09-14 - ARCHITECTURAL FIX: Use canonical data directly, no transformations
  const monsterData = useMemo(() => {
    console.log('[MonsterSheetViewer] Computing monster data from:', {
      token: token ? {
        id: token.id,
        name: token.name,
        type: token.type,
        hasActions: !!token.actions,
        actionsCount: token.actions?.length || 0,
        templateId: token.templateId,
        dashboardId: token.dashboardId
      } : null,
      effectiveTemplateId,
      templateDataAvailable: !!templateData
    });

    // ARCHITECTURAL COMPLIANCE: Use canonical data directly without transformation
    let sourceData;
    if (token) {
      // Prefer the richest embedded canonical source for monster tokens so stale
      // flattened runtime placeholders do not override real imported monster stats.
      if (token.type === 'monster') {
        const canonicalMonsterData = token.originalUnifiedEntity?.data
          || token.canonicalData
          || token.monsterData?.data
          || null;

        if (canonicalMonsterData) {
          sourceData = {
            ...(token.monsterData || {}),
            data: canonicalMonsterData
          };
          console.log('[MonsterSheetViewer] Using canonical monster data bridged from token payload');
        } else if (token.monsterData) {
          sourceData = token.monsterData;
          console.log('[MonsterSheetViewer] Using monster data from token.monsterData fallback');
        } else {
          sourceData = token;
          console.log('[MonsterSheetViewer] Using monster token data directly');
        }
      } else {
        sourceData = token;
        console.log('[MonsterSheetViewer] Using token data directly');
      }
    } else {
      sourceData = templateData;
      console.log('[MonsterSheetViewer] Using template data fallback');
    }

    if (sourceData) {
      // Extract canonical data - handle nested data structure
      const canonicalData = sourceData.data ? sourceData.data : sourceData;
      const runtimeToken = token || {};

      // date_of_change: 2025-12-04
      // rule_reference: monster_template.json → bridge canonical abilities/defense/movement/proficiencies into viewer-friendly flat fields
      const defense = canonicalData.defense || {};
      const hpNode = defense.hp || canonicalData.hp || {};
      const acNode = defense.ac || canonicalData.ac || {};
      const movementNode = canonicalData.movement || canonicalData.speed || {};
      const sensesNode = canonicalData.senses || {};
      const languagesNode = canonicalData.languages || {};
      const proficiencies = canonicalData.proficiencies || {};

      const resolvedAbilities = resolvePreferredAbilityScores({
        runtimeSources: [
          runtimeToken.abilities,
          runtimeToken.stats,
          runtimeToken
        ],
        canonicalSources: [
          canonicalData.abilities,
          canonicalData.stats,
          canonicalData
        ],
        runtimeOverrideSource: runtimeToken
      });
      const resolvedStats = buildShortAbilityBlock(resolvedAbilities);

      const parseFeet = (value) => {
        if (value == null) return undefined;
        if (typeof value === 'number') return value;
        const m = String(value).match(/(-?\d+(?:\.\d+)?)/);
        return m ? Number(m[1]) : undefined;
      };

      const buildSpeed = () => {
        // Legacy monsters: data.speed already in numeric feet
        if (canonicalData.speed) return canonicalData.speed;
        if (!movementNode || typeof movementNode !== 'object') return undefined;
        const out = {};
        Object.entries(movementNode).forEach(([key, value]) => {
          if (key === 'notes') return;
          const v = parseFeet(value);
          if (v != null) out[key] = v;
        });
        return Object.keys(out).length ? out : undefined;
      };

      const buildSenses = () => {
        if (!sensesNode || typeof sensesNode !== 'object') return canonicalData.senses || {};
        const out = {};
        Object.entries(sensesNode).forEach(([key, value]) => {
          if (key === 'passivePerception') {
            out[key] = value;
          } else {
            const v = parseFeet(value);
            if (v != null) out[key] = v;
          }
        });
        return out;
      };

      const buildLanguages = () => {
        if (!languagesNode || typeof languagesNode !== 'object') return languagesNode || {};
        const spoken = Array.isArray(languagesNode.list)
          ? languagesNode.list
          : (Array.isArray(languagesNode.spoken) ? languagesNode.spoken : []);
        const tele = parseFeet(languagesNode.telepathy);
        if (!spoken.length && tele == null) return languagesNode;
        const result = { list: spoken };
        if (tele != null) result.telepathy = tele;
        return result;
      };

      const hpCurrent = typeof hpNode === 'object'
        ? (hpNode.current ?? hpNode.average ?? undefined)
        : (typeof hpNode === 'number' ? hpNode : undefined);

      const hpMax = typeof hpNode === 'object'
        ? (hpNode.max ?? hpNode.average ?? undefined)
        : (typeof canonicalData.maxHp === 'number' ? canonicalData.maxHp : hpCurrent);

      const hitDiceVal = (typeof hpNode === 'object' && (hpNode.hitDice || hpNode.formula))
        || canonicalData.hitDice
        || '';

      const acVal = typeof acNode === 'object'
        ? (acNode.base ?? acNode.value ?? undefined)
        : (typeof acNode === 'number' ? acNode : undefined);
      const runtimeHpCurrent = firstPresentValue(
        runtimeToken.hpObj?.current,
        typeof runtimeToken.hp === 'object' ? runtimeToken.hp?.current : undefined,
        toFiniteNumber(runtimeToken.hp)
      );
      const runtimeHpMax = firstPresentValue(
        runtimeToken.hpObj?.max,
        typeof runtimeToken.hp === 'object' ? runtimeToken.hp?.max : undefined,
        toFiniteNumber(runtimeToken.maxHp)
      );
      const runtimeAc = toFiniteNumber(runtimeToken.ac);

      const challengeNode = canonicalData.challenge || canonicalData.cr || {};
      const crVal = firstPresentValue(
        runtimeToken.challengeRating,
        runtimeToken.cr,
        resolveCombatantChallengeDisplay(runtimeToken, sourceData, canonicalData)
      ) || '0';
      const xpVal = (typeof challengeNode === 'object' && typeof challengeNode.xp === 'number')
        ? challengeNode.xp
        : (canonicalData.xp ?? 0);

      const savingThrowsNode = canonicalData.savingThrows || proficiencies.savingThrows || {};
      const skillsNode = canonicalData.skills || proficiencies.skills || {};

      const rawDamageMods = Array.isArray(defense.damageModifiers) ? defense.damageModifiers : [];
      let damageResistances = canonicalData.damageResistances;
      let damageImmunities = canonicalData.damageImmunities;
      let damageVulnerabilities = canonicalData.damageVulnerabilities;
      if ((!damageResistances || !damageImmunities || !damageVulnerabilities) && rawDamageMods.length) {
        const res = [];
        const imm = [];
        const vul = [];
        rawDamageMods.forEach((mod) => {
          if (!mod || !mod.damageType) return;
          const base = String(mod.damageType);
          const q = Array.isArray(mod.qualifiers) && mod.qualifiers.length
            ? ` (${mod.qualifiers.join(', ')})`
            : '';
          const label = `${base}${q}`;
          if (mod.kind === 'resistant') res.push(label);
          else if (mod.kind === 'immune') imm.push(label);
          else if (mod.kind === 'vulnerable') vul.push(label);
        });
        if (!damageResistances) damageResistances = res;
        if (!damageImmunities) damageImmunities = imm;
        if (!damageVulnerabilities) damageVulnerabilities = vul;
      }

      // ZERO TRANSFORMATION RULE: Use canonical data format directly, only bridging where viewer expects flat fields
      const finalData = {
        ...canonicalData,
        // Preserve identifiers
        id: sourceData.id || canonicalData.id || effectiveTemplateId || 'unknown',
        dashboardId: sourceData.dashboardId || sourceData.templateId || sourceData.id,
        templateId: sourceData.templateId || sourceData.dashboardId || sourceData.id,
        entityType: 'monster',
        type: 'monster',
        name: firstPresentValue(runtimeToken.name, canonicalData.name),
        // date_of_change: 2026-04-27 - Also check monsterType and plain-string creatureType for broader canonical compatibility
        creatureType: firstPresentValue(
          runtimeToken.creatureTypeLabel,
          runtimeToken.creatureType,
          canonicalData.taxonomy?.type,
          canonicalData.creatureType?.type,
          canonicalData.creatureType?.name,
          typeof canonicalData.creatureType === 'string' ? canonicalData.creatureType : undefined,
          canonicalData.monsterType
        ),
        alignment: firstPresentValue(
          runtimeToken.alignment,
          canonicalData.taxonomy?.alignment,
          canonicalData.alignment
        ),
        size: firstPresentValue(runtimeToken.size, canonicalData.taxonomy?.size, canonicalData.size),

        // HP derived from canonical defense.hp with legacy fallbacks
        hp: runtimeHpCurrent ?? hpCurrent ?? canonicalData.hp?.average ?? canonicalData.hp?.current ?? canonicalData.hp ?? 0,
        maxHp: runtimeHpMax ?? hpMax ?? canonicalData.hp?.average ?? canonicalData.hp?.max ?? canonicalData.maxHp ?? canonicalData.hp ?? 0,
        hpObj: {
          current: runtimeHpCurrent ?? hpCurrent ?? canonicalData.hp?.current ?? canonicalData.hp?.average ?? canonicalData.hp ?? 0,
          max: runtimeHpMax ?? hpMax ?? canonicalData.hp?.average ?? canonicalData.hp?.max ?? canonicalData.maxHp ?? canonicalData.hp ?? 0
        },
        hitDice: hitDiceVal,

        // AC from canonical defense.ac.base with legacy fallbacks
        ac: runtimeAc ?? acVal ?? canonicalData.ac?.value ?? canonicalData.ac ?? 10,

        // CR / XP from canonical challenge node with legacy fallbacks
        cr: firstPresentValue(runtimeToken.challengeRating, runtimeToken.cr, crVal),
        challengeRating: firstPresentValue(runtimeToken.challengeRating, runtimeToken.cr, crVal),
        xp: xpVal,

        // Preserve canonical abilities (long keys) while also exposing short keys for stat-block rendering
        abilities: resolvedAbilities,
        stats: resolvedStats,
        actions: (Array.isArray(runtimeToken.actions) && runtimeToken.actions.length > 0) ? runtimeToken.actions : (canonicalData.actions || []),
        bonusActions: (Array.isArray(runtimeToken.bonusActions) && runtimeToken.bonusActions.length > 0) ? runtimeToken.bonusActions : (canonicalData.bonusActions || []),
        // date_of_change: 2026-04-27 - Merge specialAbilities into traits for display; many canonical monsters use specialAbilities instead of traits
        traits: (Array.isArray(runtimeToken.traits) && runtimeToken.traits.length > 0)
          ? runtimeToken.traits
          : [
              ...(canonicalData.traits || []),
              ...(canonicalData.specialAbilities || [])
            ],
        legendaryActions: (Array.isArray(runtimeToken.legendaryActions) && runtimeToken.legendaryActions.length > 0) ? runtimeToken.legendaryActions : (canonicalData.legendaryActions || []),
        reactions: (Array.isArray(runtimeToken.reactions) && runtimeToken.reactions.length > 0) ? runtimeToken.reactions : (canonicalData.reactions || []),
        spells: canonicalData.spells || null,

        // Additional canonical fields bridged into legacy viewer expectations
        skills: skillsNode || {},
        savingThrows: savingThrowsNode || {},
        senses: buildSenses(),
        languages: buildLanguages(),
        // date_of_change: 2026-04-27 - Fix undefined runtimeSpeed/canonicalSpeed; use buildSpeed() and runtime fallback
        speed: (runtimeToken.speed != null ? runtimeToken.speed : undefined) ?? buildSpeed(),
        damageResistances: damageResistances || [],
        damageImmunities: damageImmunities || [],
        damageVulnerabilities: damageVulnerabilities || [],
        conditionImmunities: canonicalData.conditionImmunities || []
      };

      console.log('[MonsterSheetViewer] Using canonical data directly:', {
        name: finalData.name,
        hp: finalData.hp,
        maxHp: finalData.maxHp,
        ac: finalData.ac,
        cr: finalData.cr,
        actionsCount: finalData.actions?.length || 0,
        dataIntegrity: 'PRESERVED_CANONICAL_FORMAT'
      });

      return finalData;
    }

    // Return null if no source available so the UI can present a clear loading/missing state
    // date_of_change: 2025-12-04
    // rule_reference: AGENTS.md zero-magic-defaults — avoid synthesizing fake monsters with default stats
    logValidation('[MonsterSheetViewer] No source monster data available in monsterData useMemo; returning null view model');
    return null;
  }, [token, effectiveTemplateId, templateData]);

  // Using utility functions from MonsterDataFormatters.js

  // Helper for formatting string attributes like senses and languages
  const formatStringAttribute = (attribute) => {
    return safeFormat(attribute) || '';
  };

  // date_of_change: 2025-08-24 - Normalize description at render-time for SRD-style data
  // Purpose: Some saved monsters/actions/traits use `desc` or `entries` instead of `description`.
  // This helper derives a displayable description without requiring re-save.
  // date_of_change: 2026-04-27 - Also synthesize a description from canonical attack data when
  // no text field exists (e.g. SRD brown-bear Bite/Claws with only attack.toHit/damage).
  const normalizeDescription = (item) => {
    if (!item || typeof item !== 'object') return '';
    if (typeof item.description === 'string' && item.description.trim()) return item.description;
    if (typeof item.text === 'string' && item.text.trim()) return item.text;
    if (typeof item.desc === 'string' && item.desc.trim()) return item.desc;
    if (Array.isArray(item.entries) && item.entries.length > 0) {
      const flatten = (arr) => arr
        .map((e) => Array.isArray(e)
          ? e.join('\n')
          : (e && typeof e === 'object' && Array.isArray(e.entries))
            ? flatten(e.entries)
            : safeFormat(e))
        .join('\n');
      return flatten(item.entries);
    }
    // Synthesize from canonical attack object: { type, toHit, reach, range, damage: [{dice,type}] }
    if (item.attack && typeof item.attack === 'object') {
      return buildDescriptionFromAttack(item.attack);
    }
    return '';
  };

  // date_of_change: 2026-04-27 - Generate D&D-style description from canonical attack data.
  // e.g. "Melee Weapon Attack: +5 to hit, reach 5 ft., one target. Hit: 8 (1d8 + 4) piercing damage."
  const buildDescriptionFromAttack = (attack) => {
    if (!attack) return '';
    const parts = [];
    // Attack type label
    const typeMap = {
      meleeWeapon: 'Melee Weapon Attack',
      rangedWeapon: 'Ranged Weapon Attack',
      meleeSpell: 'Melee Spell Attack',
      rangedSpell: 'Ranged Spell Attack',
      melee: 'Melee Weapon Attack',
      ranged: 'Ranged Weapon Attack'
    };
    const typeLabel = typeMap[attack.type] || 'Melee Weapon Attack';
    let line = `${typeLabel}: `;
    if (attack.toHit !== undefined) {
      const bonus = Number(attack.toHit);
      line += `${bonus >= 0 ? '+' : ''}${bonus} to hit, `;
    }
    if (attack.reach) line += `reach ${attack.reach} ft., `;
    if (attack.range) {
      if (typeof attack.range === 'object') {
        line += `range ${attack.range.normal || ''}${attack.range.long ? '/' + attack.range.long : ''} ft., `;
      } else {
        line += `range ${attack.range} ft., `;
      }
    }
    line += `${attack.targets || 'one target'}.`;
    parts.push(line);
    // Hit / damage
    if (Array.isArray(attack.damage) && attack.damage.length > 0) {
      const dmgParts = attack.damage.map(d => {
        const avg = estimateDiceAverage(d.dice);
        return avg !== null
          ? `${avg} (${d.dice}) ${d.type} damage`
          : `${d.dice} ${d.type} damage`;
      });
      parts.push(`Hit: ${dmgParts.join(' plus ')}.`);
    }
    return parts.join(' ');
  };

  // Estimate the average of a dice expression like "2d6+4" → 11
  const estimateDiceAverage = (expr) => {
    if (!expr || typeof expr !== 'string') return null;
    const m = expr.match(/^(\d+)d(\d+)([+-]\d+)?$/);
    if (!m) return null;
    const count = parseInt(m[1], 10);
    const sides = parseInt(m[2], 10);
    const mod = m[3] ? parseInt(m[3], 10) : 0;
    return Math.floor(count * (sides + 1) / 2 + mod);
  };

  // date_of_change: 2025-08-25 - Resolve action descriptions from traits/specialAbilities when missing (enhanced matching)
  // Looks up a similarly-named entry in traits/specialAbilities/legendaryActions when the action text is placeholder/empty.
  // Enhancements:
  //  - Placeholder detection tolerates punctuation (e.g., "No description available.")
  //  - Name matching ignores non-alphanumeric chars and supports partial matches
  const resolveActionDescription = (action, data) => {
    const base = (normalizeDescription(action) || '').trim();
    const baseLc = base.toLowerCase();
    const placeholders = ['no description available', 'n/a'];
    if (base && !placeholders.some(p => baseLc.includes(p))) return base;

    const normalizeName = (s) => (s || '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');

    const actionNameN = normalizeName(action?.name);
    if (!actionNameN) return base;

    const candidates = [
      ...(Array.isArray(data?.traits) ? data.traits : []),
      ...(Array.isArray(data?.specialAbilities) ? data.specialAbilities : []),
      ...(Array.isArray(data?.legendaryActions) ? data.legendaryActions : [])
    ];

    // Try exact normalized equality first
    let match = candidates.find(t => normalizeName(t?.name) === actionNameN);
    // Then try partial includes (either direction) to account for suffixes like "(Recharge 5–6)"
    if (!match) {
      match = candidates.find(t => {
        const n = normalizeName(t?.name);
        return n && (n.includes(actionNameN) || actionNameN.includes(n));
      });
    }

    if (match) {
      const resolved = (normalizeDescription(match) || '').trim();
      if (resolved) return resolved;
    }
    return base;
  };

  if (!monsterData) {
    return (
      <div className="monster-sheet-viewer">
        <div className="no-monster">
          <p>{effectiveTemplateId ? 'Loading or missing monster data' : 'No monster selected'}</p>
        </div>
      </div>
    );
  }

  // date_of_change: 2026-04-27 - Include size in the creature-type subtitle line (e.g. "Large Beast, Unaligned")
  const headerSize = toDisplayLabel(monsterData.size);
  const headerCreatureType = toDisplayLabel(monsterData.creatureType);
  const headerAlignment = toDisplayLabel(monsterData.alignment);

  // date_of_change: 2026-04-28 - Resolve token image for the header portrait.
  // Prefer token prop (battleroom context) then monsterData fields.
  const headerTokenImageSrc = resolveMonsterArtUrl(
    token || monsterData
  );

  const renderAbilityScores = () => {
    // date_of_change: 2025-12-04
    // rule_reference: monster_template.json abilities + AGENTS.md zero-magic-defaults — no silent fallback to 10
    if (!monsterData) {
      logValidation('[MonsterSheetViewer] renderAbilityScores called with null monsterData');
      return null;
    }

    const abilityOrder = ['str', 'dex', 'con', 'int', 'wis', 'cha'];
    const abilities = monsterData.stats || buildShortAbilityBlock(monsterData.abilities) || {};

    if (!abilities || typeof abilities !== 'object' || Object.keys(abilities).length === 0) {
      logValidation('[MonsterSheetViewer] Missing abilities block for monster', monsterData.name || monsterData.id || 'unknown');
      return (
        <div className="monster-abilities">
          <div className="monster-ability">
            <div className="ability-name">ERR</div>
            <div className="ability-score">Missing abilities data</div>
          </div>
        </div>
      );
    }

    const resolveScore = (key) => {
      const raw = abilities[key];
      if (typeof raw === 'number') return raw;
      if (raw != null) {
        const num = Number(raw);
        if (Number.isFinite(num)) return num;
      }
      logValidation('[MonsterSheetViewer] Missing or non-numeric ability score', {
        monster: monsterData.name || monsterData.id || 'unknown',
        ability: key,
        value: raw
      });
      return null;
    };

    return (
      <div className="monster-abilities">
        {abilityOrder.map((ability) => {
          const score = resolveScore(ability);
          if (score == null) {
            return (
              <div key={ability} className="monster-ability">
                <div className="ability-name">{ability.toUpperCase()}</div>
                <div className="ability-score">ERR</div>
              </div>
            );
          }
          const modifier = getAbilityModifier(score);
          return (
            <div key={ability} className="monster-ability">
              <div className="ability-name">{ability.toUpperCase()}</div>
              <div className="ability-score">{score} ({formatModifier(modifier)})</div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCombatStats = () => {
    if (!monsterData) return null;
    
    // DEBUG: Log combat stats data before rendering
    console.log('[DEBUG] renderCombatStats data:', {
      ac: monsterData.ac,
      hp: monsterData.hp,
      hpObj: monsterData.hpObj,
      hitDice: monsterData.hitDice,
      speed: monsterData.speed,
      savingThrows: monsterData.savingThrows,
      skills: monsterData.skills,
      senses: monsterData.senses,
      languages: monsterData.languages,
      challengeRating: monsterData.challengeRating || monsterData.cr
    });

    // @date_of_change 2025-09-14 - Handle canonical CR format
    const cr = monsterData.cr || monsterData.challengeRating || '0';
    const crValue = typeof cr === 'string' ? convertCRToNumber(cr) : cr;
    const xp = monsterData.xp || calculateXPFromCR(crValue);
    
    // @date_of_change 2025-09-14 - Fix HP display from canonical data
    const { hpObj, hitDice } = monsterData;
    const currentHP = hpObj?.current || monsterData.hp || 0;
    const maxHP = hpObj?.max || monsterData.maxHp || 1;
    const hitDiceText = hitDice || monsterData.hitDice || '';
    
    const speedText = typeof monsterData.speed === 'object'
      ? Object.entries(monsterData.speed)
          .map(([type, value]) => type === 'walk' ? `${value} ft.` : `${type.charAt(0).toUpperCase() + type.slice(1)} ${value} ft.`)
          .join(', ')
      : (monsterData.speed || 'N/A');

    const sensesText = formatStringAttribute(
      typeof monsterData.senses === 'object'
        ? Object.entries(monsterData.senses)
            .map(([key, value]) => key === 'passivePerception' ? `passive Perception ${value}` : `${key} ${value} ft.`)
            .join(', ')
        : monsterData.senses
    ) || 'None';

    const languagesText = formatStringAttribute(
      typeof monsterData.languages === 'object'
        ? (monsterData.languages.list || []).join(', ') +
          (monsterData.languages.telepathy ? `, telepathy ${monsterData.languages.telepathy} ft.` : '')
        : monsterData.languages
    ) || 'None';

    const savingThrowsText = monsterData.savingThrows && Object.keys(monsterData.savingThrows).length > 0
      ? Object.entries(monsterData.savingThrows).map(([ab, bonus]) => {
          const v = typeof bonus === 'object' ? (bonus.average || bonus.value || 0) : bonus;
          return `${ab.substring(0, 3).toUpperCase()} +${v}`;
        }).join(', ')
      : null;

    const skillsText = monsterData.skills && Object.keys(monsterData.skills).length > 0
      ? Object.entries(monsterData.skills).map(([sk, bonus]) => {
          const v = typeof bonus === 'object' ? (bonus.average || bonus.value || 0) : bonus;
          return `${sk.charAt(0).toUpperCase() + sk.slice(1)} +${v}`;
        }).join(', ')
      : null;

    return (
      <div className="monster-combat-stats">
        <p className="cs-line"><span className="cs-label">AC</span> {monsterData.ac || 'N/A'}</p>
        <p className="cs-line"><span className="cs-label">HP</span> {currentHP}/{maxHP}{hitDiceText ? ` (${hitDiceText})` : ''}</p>
        <p className="cs-line"><span className="cs-label">Speed</span> {speedText}</p>
        {savingThrowsText && (
          <p className="cs-line"><span className="cs-label">Saving Throws</span> {savingThrowsText}</p>
        )}
        {skillsText && (
          <p className="cs-line"><span className="cs-label">Skills</span> {skillsText}</p>
        )}
        {monsterData.damageResistances?.length > 0 && (
          <p className="cs-line"><span className="cs-label">Damage Resistances</span> {monsterData.damageResistances.join(', ')}</p>
        )}
        {monsterData.damageImmunities?.length > 0 && (
          <p className="cs-line"><span className="cs-label">Damage Immunities</span> {monsterData.damageImmunities.join(', ')}</p>
        )}
        {monsterData.damageVulnerabilities?.length > 0 && (
          <p className="cs-line"><span className="cs-label">Damage Vulnerabilities</span> {monsterData.damageVulnerabilities.join(', ')}</p>
        )}
        {monsterData.conditionImmunities?.length > 0 && (
          <p className="cs-line"><span className="cs-label">Condition Immunities</span> {monsterData.conditionImmunities.join(', ')}</p>
        )}
        <p className="cs-line"><span className="cs-label">Senses</span> {sensesText}</p>
        <p className="cs-line"><span className="cs-label">Languages</span> {languagesText}</p>
        <p className="cs-line"><span className="cs-label">Challenge</span> {safeFormat(monsterData.challengeRating || monsterData.cr)} ({xp} XP){monsterData.proficiencyBonus ? <> &nbsp;<span className="cs-label">Proficiency Bonus</span> +{monsterData.proficiencyBonus}</> : ''}</p>
      </div>
    );
  };

  const renderActions = () => {
    // DEBUG: Log actions data before rendering
    // Debug log for tracking action rendering
    console.log('[DEBUG] renderActions data:', monsterData.actions);

    // Using utility functions from MonsterDataFormatters.js
    
    const renderDamageEntries = (action) => {
      // @date_of_change 2025-09-14 - Handle canonical damage structure from monster JSON
      const attack = action.attack;

      // Use canonical damage array from attack object
      if (attack?.damage && Array.isArray(attack.damage)) {
        return attack.damage.map((damageEntry, i) => (
          <span key={i} className="damage-entry">
            {i > 0 && ' plus '}
            {damageEntry.dice} {damageEntry.type} damage
            {damageEntry.conditional && (
              <em className="damage-conditional"> ({damageEntry.conditional})</em>
            )}
          </span>
        ));
      }

      // Fallback to legacy damageEntries format
      if (Array.isArray(action.damageEntries) && action.damageEntries.length > 0) {
        return action.damageEntries.map((entry, i) => (
          <span key={i} className="damage-entry">
            {i > 0 && ' plus '}
            {formatDamage(entry)}
          </span>
        ));
      }

      // Fallback to simple damage/damageType
      else if (action.damage && action.damageType) {
        // Handle potential object values
        const damage = safeFormat(action.damage);
        const damageType = safeFormat(action.damageType);
        return <span className="damage-entry">{damage} {damageType} damage</span>;
      }

      return null;
    };
    
    const renderAttackDetails = (action) => {
      // @date_of_change 2025-09-14 - Handle canonical attack structure from monster JSON
      const attack = action.attack;
      if (!attack || (!attack.toHit && !attack.type)) return null;

      // Process attack type using canonical format
      const attackType = attack.type || action.attackType;
      const attackTypeText = formatAttackType(attackType);

      let attackText = '';

      // Attack type
      if (attackTypeText) {
        attackText += `${attackTypeText}: `;
      }

      // Attack bonus - use canonical toHit field
      const attackBonus = attack.toHit || action.attackBonus;
      if (attackBonus !== undefined) {
        const bonus = typeof attackBonus === 'object' ?
          parseInt(safeFormat(attackBonus)) : attackBonus;
        attackText += `${bonus >= 0 ? '+' : ''}${bonus} to hit, `;
      }

      // Reach for melee attacks - use canonical reach field
      const reach = attack.reach || action.reach;
      if (attackType?.includes('melee') && reach) {
        const reachValue = typeof reach === 'object' ? safeFormat(reach) : reach;
        attackText += `reach ${reachValue} ft., `;
      }
      // Range for ranged attacks - use canonical range field
      else if (attackType?.includes('ranged') && (attack.range || action.range)) {
        const range = attack.range || action.range;
        if (typeof range === 'object') {
          if (!range.normal && !range.long) {
            // If it's some other kind of object
            attackText += `range ${safeFormat(range)} ft., `;
          } else {
            // Proper range object
            const normal = safeFormat(range.normal);
            const long = safeFormat(range.long);

            if (normal && long) {
              attackText += `range ${normal}/${long} ft., `;
            } else if (normal) {
              attackText += `range ${normal} ft., `;
            }
          }
        } else {
          // Simple range value
          attackText += `range ${range} ft., `;
        }
      }

      // Targets - use canonical format
      const targets = attack.targets || action.targets;
      if (targets) {
        const targetText = safeFormat(targets);
        attackText += `${targetText}. `;
      } else {
        attackText += 'one target. ';
      }

      return <div className="attack-details">{attackText}</div>;
    };
    
    const renderSavingThrow = (action) => {
      // @date_of_change 2025-09-14 - Handle canonical save structure from monster JSON
      const save = action.save || action.savingThrow;
      if (!save) return null;

      // Using safeFormat from MonsterDataFormatters.js
      const saveType = typeof save.ability === 'object' ? safeFormat(save.ability) : (save.ability || save.type);
      const saveText = formatSaveType(saveType);
      const dc = safeFormat(save.dc);
      const success = (save.success || '').toString();
      const notes = safeFormat(save.notes);
      const failEffect = safeFormat(save.failEffect);

      // Map common success codes to human text
      let successText = '';
      if (success === 'half') successText = 'half damage';
      else if (success === 'none') successText = 'no damage';
      else if (success === 'other') successText = 'other effect';

      return (
        <div className="save-details">
          <strong>{saveText} save</strong> DC {dc}
          {successText && <span>, on success: {successText}</span>}
          {notes && <span>, {notes}</span>}
          {failEffect && <span>. On failure: {failEffect}</span>}
        </div>
      );
    };

    const renderAreaInfo = (action) => {
      const { areaType, areaSize, areaUnit, areaTargets } = action || {};
      if (!areaType || !areaSize) return null;
      const unit = areaUnit || 'ft';
      const targetText = areaTargets ? `, ${safeFormat(areaTargets)}` : '';
      return (
        <div className="area-details">
          <strong>Area:</strong> {safeFormat(areaSize)} {unit} {safeFormat(areaType)}{targetText}
        </div>
      );
    };
    
    const renderRecharge = (action) => {
      if (!action) return null;
      const text = action.rechargeText && safeFormat(action.rechargeText);
      const min = action.rechargeMin;
      const max = action.rechargeMax;
      let display = text;
      if (!display && (min || max)) {
        if (min && max && min !== max) display = `Recharge ${min}-${max}`;
        else if (min) display = `Recharge ${min}`;
      }
      if (!display) return null;
      return (
        <div className="recharge-details">
          <em>{display}</em>
        </div>
      );
    };
    
    const renderHitSection = (action) => {
      // @date_of_change 2025-09-14 - Handle canonical attack structure from monster JSON
      const attack = action.attack;

      // Check if we have either hit text, damage entries, or canonical attack structure
      if ((!action.hit && !action.onHit) &&
          !attack?.damage &&
          !(Array.isArray(action.damageEntries) && action.damageEntries.length > 0)) {
        return null;
      }

      // Format the hit text if available
      let formattedHit = null;
      const hit = action.hit || action.onHit;

      if (hit) {
        // Using safeFormat from MonsterDataFormatters.js
        formattedHit = safeFormat(hit);
      }

      // Get damage entries if available
      const damageEntries = renderDamageEntries(action);

      // Format additional effects if available (check both canonical and legacy)
      let additionalEffects = null;
      const effects = attack?.rider || attack?.notes || action.additionalEffects;
      if (effects) {
        const effectsText = safeFormat(effects);
        additionalEffects = <span className="additional-effects">. {effectsText}</span>;
      }

      return (
        <div className="hit-section">
          <span className="hit-prefix"><strong>Hit:</strong> </span>
          {formattedHit && <span>{formattedHit} </span>}
          {damageEntries}
          {additionalEffects}
        </div>
      );
    };
    
    // Check if actions exist
    if (!monsterData.actions || !Array.isArray(monsterData.actions) || monsterData.actions.length === 0) {
      return (
        <div className="monster-actions">
          <h4>Actions</h4>
          <div className="no-actions">No actions available</div>
        </div>
      );
    }
    
    // date_of_change: 2026-04-27 - Rewrite action rendering for D&D stat-block style:
    //  - Bold italic name inline with description paragraph (classic 5e layout)
    //  - More robust description resolution: try description/text/desc fields directly
    //    before falling back to resolveActionDescription cross-reference
    return (
      <div className="monster-actions">
        <h4>Actions</h4>
        {monsterData.actions.map((action, index) => {
          // Resolve the best available description text
          const descText = normalizeDescription(action)
            || resolveActionDescription(action, monsterData)
            || '';

          return (
            <div key={index} className="monster-action">
              <p className="action-block">
                <span className="action-name">{safeFormat(action.name)}.</span>{' '}
                {descText ? (
                  <span className="action-description">{safeFormat(descText)}</span>
                ) : (
                  <span className="action-description">
                    {renderAttackDetails(action)}
                    {renderHitSection(action)}
                  </span>
                )}
              </p>
              {renderSavingThrow(action)}
              {renderAreaInfo(action)}
              {renderRecharge(action)}
            </div>
          );
        })}
      </div>
    );
  };

  // date_of_change: 2026-04-27 - Rewrite traits/reactions/legendary for D&D stat-block inline style:
  //  bold italic name + period, then description in same paragraph (classic 5e layout).
  const renderTraitsAndReactions = () => {
    return (
      <div className="monster-special">
        {monsterData.bonusActions && monsterData.bonusActions.length > 0 && (
          <div className="bonus-actions-section">
            <h4>Bonus Actions</h4>
            {monsterData.bonusActions.map((action, index) => (
              <div key={action.id || index} className="monster-bonus-action">
                <p className="bonus-action-block">
                  <span className="bonus-action-name">{safeFormat(action.name)}.</span>{' '}
                  <span className="bonus-action-description">{safeFormat(normalizeDescription(action))}</span>
                </p>
              </div>
            ))}
          </div>
        )}

        {monsterData.traits && monsterData.traits.length > 0 && (
          <div className="traits-section">
            <h4>Traits</h4>
            {monsterData.traits.map((trait, index) => (
              <div key={index} className="monster-trait">
                <p className="trait-block">
                  <span className="trait-name">{safeFormat(trait.name)}.</span>{' '}
                  <span className="trait-description">{safeFormat(normalizeDescription(trait))}</span>
                </p>
              </div>
            ))}
          </div>
        )}
        
        {monsterData.reactions && monsterData.reactions.length > 0 && (
          <div className="reactions-section">
            <h4>Reactions</h4>
            {monsterData.reactions.map((reaction, index) => (
              <div key={index} className="monster-reaction">
                <p className="reaction-block">
                  <span className="reaction-name">{safeFormat(reaction.name)}.</span>{' '}
                  <span className="reaction-description">{safeFormat(normalizeDescription(reaction))}</span>
                </p>
              </div>
            ))}
          </div>
        )}
        
        {monsterData.legendaryActions && (monsterData.legendaryActions.length > 0 || monsterData.legendaryActions.options) && (
          <div className="legendary-actions-section">
            <h4>Legendary Actions</h4>
            {monsterData.legendaryActions.text && (
              <p className="legendary-actions-description">{safeFormat(monsterData.legendaryActions.text)}</p>
            )}
            {monsterData.legendaryActions.options && monsterData.legendaryActions.options.map((action, index) => (
              <div key={index} className="legendary-action">
                <p className="legendary-block">
                  <span className="legendary-action-name">
                    {safeFormat(action.name)}
                    {action.cost && action.cost > 1 && <span className="legendary-cost"> (Costs {action.cost} Actions)</span>}
                    .
                  </span>{' '}
                  <span className="legendary-action-description">{safeFormat(action.text || normalizeDescription(action))}</span>
                </p>
              </div>
            ))}
            {Array.isArray(monsterData.legendaryActions) && monsterData.legendaryActions.map((action, index) => (
              <div key={index} className="legendary-action">
                <p className="legendary-block">
                  <span className="legendary-action-name">{safeFormat(action.name)}.</span>{' '}
                  <span className="legendary-action-description">{safeFormat(normalizeDescription(action))}</span>
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── Shared card markup ────────────────────────────────────────
  // date_of_change: 2026-04-27 - Single scrollable stat block; tabs removed.
  // date_of_change: 2026-04-28 - Header split into text-left / token-image-right layout
  //   matching the Aeromancer reference (name + subtitle left, circular portrait top-right).
  const viewerCard = (
    <div className="monster-sheet-viewer">
      <div className="monster-header">
        <div className="monster-header-text">
          <h2>{monsterData.name || 'Unknown Monster'}</h2>
          <div className="monster-type">
            {/* date_of_change: 2026-04-27 - D&D-standard subtitle: "Size Type, Alignment" */}
            {headerSize ? `${headerSize} ` : ''}{headerCreatureType || 'Unknown Creature'}
            {headerAlignment ? `, ${headerAlignment}` : ''}
          </div>
          {/* date_of_change: 2025-08-24 - Use normalized challenge fields for header CR to avoid showing 0 when data has cr */}
          <div className="challenge-rating">CR {(
            (monsterData.challenge && (monsterData.challenge.cr ?? monsterData.challenge.rating))
            || monsterData.cr
            || monsterData.challengeRating
            || '0'
          )}</div>
        </div>
        {/* date_of_change: 2026-04-28 - Circular token portrait floated top-right */}
        {!portraitError && (
          <div className="monster-header-portrait">
            <img
              src={headerTokenImageSrc}
              alt={monsterData.name || 'Monster token'}
              className="monster-header-token"
              onError={() => setPortraitError(true)}
            />
          </div>
        )}
      </div>

      <div className="stat-block-body">
        {renderAbilityScores()}
        {renderCombatStats()}
        <div className="stat-block-divider" />
        {renderActions()}
        <div className="stat-block-divider" />
        {renderTraitsAndReactions()}
      </div>
    </div>
  );

  // date_of_change: 2026-04-27 - Standalone page mode wraps the card in
  // a full-page shell with navigation, constrained width, and proper chrome.
  if (isStandalonePage) {
    return (
      <div className="monster-viewer-page">
        <div className="monster-viewer-page__header">
          <button
            onClick={() => navigate('/')}
            className="monster-viewer-page__back"
            type="button"
          >
            ← Back to Dashboard
          </button>
          <div className="monster-viewer-page__title">
            <h1>{monsterData.name || 'Monster'}</h1>
          </div>
          <button
            onClick={() => navigate(`/monster/edit/${routeId}`)}
            className="monster-viewer-page__edit"
            type="button"
          >
            Edit Monster
          </button>
        </div>
        <div className="monster-viewer-page__body">
          {viewerCard}
        </div>
      </div>
    );
  }

  return viewerCard;
};

export default MonsterSheetViewer;
